import type { HelpLocale } from './help';

/**
 * The TASK strings, in every language the app ships (V0.50; renamed V0.51.1).
 *
 * ── THE WORD IS "TASK", THE CODE STILL SAYS "TODO" (V0.51.1) ──────────────────────────────
 *
 * Every English sentence a person reads here says *task*; every identifier around it — this
 * file's own name, `TODO_I18N`, `todoText`, the `Todo` table, `VIEW_TODOS`/`EDIT_TODOS`, the
 * `/api/todos` routes, the `todo_assigned`/`todo_done` feed kinds, the `todo.*` audit keys —
 * keeps `todo`. That split is deliberate and `lib/todos.ts`'s header states why: the word lives
 * in more than a thousand places across the tree INCLUDING layers that are already STORED in the
 * database, so a full rename would mean either two names forever or a migration of permission
 * VALUES inside every role row. The UI is the half a person sees and the only half that had the
 * wrong word — French and German already said tâche and Aufgabe here.
 *
 * ── WHY THIS FILE RENDERS ENGLISH TODAY, AND SAYS SO ──────────────────────────────────────
 *
 * The four i18n modules beside this one (help, chat, presence, privacy) hold PROSE: pages and
 * tooltips a reader dwells on. Everything else in this application's chrome — "My assignments",
 * "Favorites", "Description", "Responsibles", every button in the top bar and every heading in
 * the dashboard — is a hardcoded English literal, in 57 component files. There is no `t()` and
 * there is no key convention, because nothing has ever needed one.
 *
 * So "translate the new strings through the mechanism the app already uses" has no honest
 * answer for a section of buttons and labels. Wiring these to `s.currentLocale` would put a
 * French "Tâches" between an English "Description" and an English "Documentation" — a section
 * that looks broken rather than translated, and a worse experience for the French reader than
 * the English it replaced.
 *
 * The decision, taken with the user: WRITE the translations now, RESOLVE English until the
 * chrome as a whole has a locale. The strings are here, reviewable, and turning them on is the
 * one-line change marked below — not a re-translation six months from now by somebody
 * reconstructing what these controls do. TRANSLATION-PLAN.md holds the plan for that pass.
 *
 * Follows `i18n/presence.ts` exactly otherwise: one flat record, a `todoText(key, locale)`
 * reader, English for an unknown locale. A `%s` / `%n` in a value is filled by the caller
 * (`fillText` below) rather than by string concatenation, so the word order stays the
 * translator's — French and German do not put the count where English does.
 */
export const TODO_I18N = {
  /* ---- the node popup's section ---- */
  section: { en: 'Tasks', fr: 'Tâches', de: 'Aufgaben' },
  /** The inline composer at the foot of the list. */
  addPlaceholder: {
    en: 'Add a task and press Enter…',
    fr: 'Ajoutez une tâche et appuyez sur Entrée…',
    de: 'Aufgabe hinzufügen und Eingabetaste drücken…',
  },
  /** The same box, aimed under a parent (V0.51.1). `%s` is the parent's title. */
  addChildPlaceholder: {
    en: 'Add a sub-task to “%s”…',
    fr: 'Ajoutez une sous-tâche à « %s »…',
    de: 'Teilaufgabe zu „%s“ hinzufügen…',
  },
  empty: {
    en: 'No tasks here yet.',
    fr: 'Aucune tâche ici pour l’instant.',
    de: 'Hier gibt es noch keine Aufgaben.',
  },
  emptyReadOnly: {
    en: 'No tasks on this node.',
    fr: 'Aucune tâche sur ce nœud.',
    de: 'Keine Aufgaben an diesem Knoten.',
  },

  /* ---- the row's controls ---- */
  assignee: { en: 'Assignee', fr: 'Responsable', de: 'Zuständig' },
  unassigned: { en: 'Unassigned', fr: 'Non attribuée', de: 'Nicht zugewiesen' },
  due: { en: 'Due', fr: 'Échéance', de: 'Fällig' },
  overdue: { en: 'Overdue', fr: 'En retard', de: 'Überfällig' },
  edit: { en: 'Edit this task', fr: 'Modifier cette tâche', de: 'Diese Aufgabe bearbeiten' },
  remove: { en: 'Delete this task', fr: 'Supprimer cette tâche', de: 'Diese Aufgabe löschen' },
  reorder: { en: 'Drag to re-order', fr: 'Faites glisser pour réordonner', de: 'Zum Umsortieren ziehen' },
  save: { en: 'Save', fr: 'Enregistrer', de: 'Speichern' },
  cancel: { en: 'Cancel', fr: 'Annuler', de: 'Abbrechen' },
  notesPlaceholder: { en: 'Notes (optional)', fr: 'Notes (facultatif)', de: 'Notizen (optional)' },
  /** The tooltip on an expandable row's title (V0.50.1). */
  showDescription: {
    en: 'Show the description',
    fr: 'Afficher la description',
    de: 'Beschreibung anzeigen',
  },
  titlePlaceholder: { en: 'What needs doing?', fr: 'Que faut-il faire ?', de: 'Was ist zu tun?' },
  /** The checkbox's tooltip, for a row this reader may not tick. */
  notYours: {
    en: 'Only the assignee can tick this off',
    fr: 'Seule la personne responsable peut cocher cette tâche',
    de: 'Nur die zuständige Person kann dies abhaken',
  },

  /* ---- sub-tasks (V0.51.1) ---- */
  subtask: { en: 'sub-task', fr: 'sous-tâche', de: 'Teilaufgabe' },
  subtasks: { en: 'sub-tasks', fr: 'sous-tâches', de: 'Teilaufgaben' },
  subtasksLabel: { en: 'Sub-tasks', fr: 'Sous-tâches', de: 'Teilaufgaben' },
  addSub: { en: 'Add a sub-task', fr: 'Ajouter une sous-tâche', de: 'Teilaufgabe hinzufügen' },
  showSubtasks: { en: 'Show the sub-tasks', fr: 'Afficher les sous-tâches', de: 'Teilaufgaben anzeigen' },
  hideSubtasks: { en: 'Hide the sub-tasks', fr: 'Masquer les sous-tâches', de: 'Teilaufgaben ausblenden' },
  /** The `n/m` pill's tooltip, and the detail card's row: `%d` done of `%t`. */
  subtasksDone: { en: '%d of %t done', fr: '%d sur %t terminées', de: '%d von %t erledigt' },
  /** A parent's checkbox ticks or reopens every child with it. */
  completeAll: {
    en: 'Complete every sub-task',
    fr: 'Terminer toutes les sous-tâches',
    de: 'Alle Teilaufgaben abschließen',
  },
  reopenAll: {
    en: 'Reopen every sub-task',
    fr: 'Rouvrir toutes les sous-tâches',
    de: 'Alle Teilaufgaben wieder öffnen',
  },
  /** The inline confirm before a delete. `%s` the title, `%n` the sub-task count. */
  confirmDelete: { en: 'Delete “%s”?', fr: 'Supprimer « %s » ?', de: '„%s“ löschen?' },
  confirmDeleteParent1: {
    en: 'Delete this task and its sub-task?',
    fr: 'Supprimer cette tâche et sa sous-tâche ?',
    de: 'Diese Aufgabe und ihre Teilaufgabe löschen?',
  },
  confirmDeleteParent: {
    en: 'Delete this task and its %n sub-tasks?',
    fr: 'Supprimer cette tâche et ses %n sous-tâches ?',
    de: 'Diese Aufgabe und ihre %n Teilaufgaben löschen?',
  },
  deleteVerb: { en: 'Delete', fr: 'Supprimer', de: 'Löschen' },
  /** A child due AFTER the parent it belongs to. `%s` is the parent's day. */
  dueAfterParent: {
    en: 'Due after its parent (%s)',
    fr: 'Échéance postérieure à celle de sa tâche parente (%s)',
    de: 'Fällig nach der übergeordneten Aufgabe (%s)',
  },

  /* ---- the detail card (V0.51.1) ---- */
  detailAssigned: { en: 'Assigned to', fr: 'Attribuée à', de: 'Zugewiesen an' },
  detailNobody: { en: 'nobody yet', fr: 'personne pour l’instant', de: 'noch niemandem' },
  detailNoDate: { en: 'no date', fr: 'sans date', de: 'ohne Termin' },
  detailWrittenBy: { en: 'Written by', fr: 'Écrite par', de: 'Geschrieben von' },
  detailOn: { en: 'on', fr: 'le', de: 'am' },
  detailDone: { en: 'Done', fr: 'Terminée', de: 'Erledigt' },
  detailBy: { en: 'by', fr: 'par', de: 'von' },
  noNotes: { en: 'No notes.', fr: 'Aucune note.', de: 'Keine Notizen.' },
  hasNotes: { en: 'Has notes', fr: 'Contient des notes', de: 'Enthält Notizen' },
  editButton: { en: 'Edit', fr: 'Modifier', de: 'Bearbeiten' },
  editHint: {
    en: 'or double-click the row',
    fr: 'ou double-cliquez sur la ligne',
    de: 'oder die Zeile doppelklicken',
  },
  /** The author pill's tooltip: `%s` the full name, `%d` the day it was written. */
  writtenBy: { en: 'Written by %s on %d', fr: 'Écrite par %s le %d', de: 'Geschrieben von %s am %d' },

  /* ---- the hint line under the list (V0.51.1) ---- */
  hintRead: {
    en: 'Click a row for its details',
    fr: 'Cliquez sur une ligne pour ses détails',
    de: 'Eine Zeile anklicken zeigt ihre Details',
  },
  hintEdit: {
    en: 'Click a row for its details · double-click to edit · Tab in the box indents a sub-task',
    fr: 'Cliquez sur une ligne pour ses détails · double-cliquez pour la modifier · Tab dans le champ crée une sous-tâche',
    de: 'Eine Zeile anklicken zeigt ihre Details · Doppelklick bearbeitet sie · Tab im Feld rückt eine Teilaufgabe ein',
  },
  /** The editor's shortcut to put the task on your own list (V0.51.1). */
  assignMe: { en: 'Assign to me', fr: 'M’attribuer cette tâche', de: 'Mir zuweisen' },

  /* ---- relative days, for the row and the date picker (V0.51.1) ---- */
  today: { en: 'Today', fr: 'Aujourd’hui', de: 'Heute' },
  tomorrow: { en: 'Tomorrow', fr: 'Demain', de: 'Morgen' },
  yesterday: { en: 'Yesterday', fr: 'Hier', de: 'Gestern' },

  /* ---- the My items pane ---- */
  myTodos: { en: 'My tasks', fr: 'Mes tâches', de: 'Meine Aufgaben' },
  myEmpty: {
    en: 'Nothing assigned to you yet.',
    fr: 'Rien ne vous est encore attribué.',
    de: 'Ihnen ist noch nichts zugewiesen.',
  },
  via: { en: 'via', fr: 'via', de: 'über' },
  locate: { en: 'Show this on the map', fr: 'Afficher sur la carte', de: 'Auf der Karte anzeigen' },
  collapse: { en: 'Collapse this section', fr: 'Réduire cette section', de: 'Diesen Abschnitt einklappen' },
  expand: { en: 'Expand this section', fr: 'Développer cette section', de: 'Diesen Abschnitt ausklappen' },
} as const;

export type TodoKey = keyof typeof TODO_I18N;

/**
 * Read one string.
 *
 * THE ONE LINE THAT TURNS TRANSLATION ON. Today every caller is served English, whatever it
 * passes, because the chrome around these strings has no locale of its own — see the header.
 * When the chrome pass lands, delete the `'en'` below and let `locale` through; nothing else
 * in this feature changes, and every call site already passes the locale it should use.
 */
export function todoText(key: TodoKey, _locale: string): string {
  const s = TODO_I18N[key] as Record<string, string>;
  const locale: HelpLocale = 'en';
  return s[locale] ?? s.en;
}

/**
 * Fill a string's placeholders.
 *
 * The values carry `%s`, `%n`, `%d`, `%t` rather than being assembled from fragments, because a
 * sentence built by concatenation has English's word order baked into the code and no
 * translator can move the count to where their language puts it. One left-to-right pass, so
 * text that arrives through a value is never itself re-scanned for placeholders.
 */
export function fillText(template: string, values: Record<string, string | number>): string {
  let out = '';
  let i = 0;
  while (i < template.length) {
    const ch = template[i];
    if (ch === '%' && i + 1 < template.length) {
      const key = template[i + 1];
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        out += String(values[key]);
        i += 2;
        continue;
      }
    }
    out += ch;
    i += 1;
  }
  return out;
}

/** `todoText` with the placeholders filled — the shape nearly every caller wants. */
export const todoTextf = (
  key: TodoKey,
  locale: string,
  values: Record<string, string | number>,
): string => fillText(todoText(key, locale), values);
